import { useCallback } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchTrackingDrivers,
  createTrackingDriver,
  updateTrackingDriver,
  deleteTrackingDriver,
  type TrackingDriver,
  type DriverCreatePayload,
  type DriverUpdatePayload,
} from "../services/tracking/driversService";

const KEY = "tracking-drivers";

export function useTrackingDrivers() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, error, isLoading, mutate } = useSWR<TrackingDriver[]>(
    token ? [KEY, token] : null,
    () => fetchTrackingDrivers(token),
    { revalidateOnFocus: false }
  );

  const refetch = useCallback(() => mutate(), [mutate]);
  const list = Array.isArray(data) ? data : [];

  const create = useCallback(
    async (payload: DriverCreatePayload) => {
      const created = await createTrackingDriver(token, payload);
      await mutate();
      return created;
    },
    [token, mutate]
  );

  const update = useCallback(
    async (id: string, payload: DriverUpdatePayload) => {
      const updated = await updateTrackingDriver(token, id, payload);
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteTrackingDriver(token, id);
      await mutate();
    },
    [token, mutate]
  );

  return {
    drivers: list,
    isLoading,
    error,
    refetch,
    create,
    update,
    remove,
    accessToken: token,
  };
}

export type { TrackingDriver, DriverCreatePayload, DriverUpdatePayload };
